import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NativeButtonType } from '@app/components/button';
import { CardComponent } from '@app/components/card';
import { FormActionButtonComponent } from '@app/components/form-action-button';
import {
  InputComponent,
  InputDirection,
  InputFieldSpacing,
  InputSize,
  InputType,
  InputVariant,
  InputWidth,
} from '@app/components/input';
import { SelectDropdownComponent } from '@app/components/select-dropdown';
import { SpinnerComponent } from '@app/components/spinner';
import {
  TextInputComponent,
  TextInputDirection,
  TextInputLabelSize,
  TextInputVariant,
  TextInputWidth,
} from '@app/components/text-input';
import { ToastService, ToastType } from '@app/components/toast';
import { ToggleComponent, ToggleHexColor } from '@app/components/toggle';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { Logger } from '@app/services/logger/logger.service';
import { SaveChangesService } from '@app/services/save-changes/save-changes.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { TenantValidator } from '@app/utils/validators/tenant-validator';
import { TranslatePipe } from '@ngx-translate/core';
import {
  EnterpriseEntityFeatureName,
  EnterpriseTenant,
  GympassFeatureConfig,
  GympassTier,
} from '@skyfit/core-data-library';
import { switchMap } from 'rxjs/operators';
import { GympassFeatureComponent } from './gympass-feature/gympass-feature.component';

interface FeatureConfiguration {
  key: EnterpriseEntityFeatureName;
  title: string;
  value: boolean;
  description: string;
  config?: Record<string, unknown>;
}

const MIN_SESSION_TIMEOUT_SECONDS = 300;
const MAX_SESSION_TIMEOUT_SECONDS = 86_400;

@Component({
  selector: 'app-tenant-features',
  standalone: true,
  imports: [
    CardComponent,
    ToggleComponent,
    FormActionButtonComponent,
    SelectDropdownComponent,
    TextInputComponent,
    InputComponent,
    TranslatePipe,
    GympassFeatureComponent,
    SpinnerComponent,
    CommonModule,
  ],
  templateUrl: './tenant-features.component.html',
  styleUrl: './tenant-features.component.css',
})
export class TenantFeaturesComponent implements OnInit {
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly translatorService = inject(TranslatorService);
  private readonly toastService = inject(ToastService);
  private readonly tenantValidator = new TenantValidator();
  private readonly logger = inject(Logger);
  private readonly saveChangesService = inject(SaveChangesService);
  public readonly tenantFeatures = this.adminTenantService.getTenantFeatures();
  readonly destroyRef = inject(DestroyRef);
  GympassTier = GympassTier;
  employerProviderSelected = signal('');
  flexTileEnabled = false;
  mfaPinEnabled = false;
  codeToJoinMessageValue = signal('');
  sessionTimeoutValue = signal(
    this.convertSecondsToMin(MIN_SESSION_TIMEOUT_SECONDS)
  );
  monthlyPrice = signal(
    this.adminTenantService.tenant()?.config?.memberPaid?.config
      ?.priceIdMonthly ?? this.GympassTier.None
  );
  isChildTenant = computed(
    () => this.adminTenantService.tenant()?.isFriendsAndFamilyTenant ?? false
  );
  monthlyPrices = this.adminTenantService.tenantMonthlyPricing;
  private readonly featuresConfig = signal<FeatureConfiguration[]>([]);

  configurationFeatures = computed(() => {
    const features = this.featuresConfig();
    if (this.isChildTenant()) {
      return features.filter(
        (f) => f.key !== EnterpriseEntityFeatureName.friendsAndFamily
      );
    }
    return features;
  });
  readMode = signal(true);
  isSaving = signal(false);
  isConfigurationsValid = computed(() => {
    if (this.readMode()) return true;
    const errors = this.errorMessages();
    return !Object.values(errors).some((v) => v);
  });
  gympassFeatureConfig = signal({} as GympassFeatureConfig);
  isLoadingFriendsAndFamilyConfig = signal(false);
  isLoadingEmployerProviders = signal(false);
  friendsAndFamilyTenant = signal({} as EnterpriseTenant);
  friendsAndFamilyTenantGympassConfig = signal({} as GympassFeatureConfig);
  monthlyPriceError = computed(
    () =>
      this.tenantValidator.validateMonthlyPrice(this.monthlyPrice())
        .validationObj.error.monthlyPrice ?? ''
  );
  friendsAndFamilyMonthlyPrice = signal(
    this.friendsAndFamilyTenant()?.config?.memberPaid?.config?.priceIdMonthly ??
      this.GympassTier.None
  );
  employerProviders = [];
  employerProvidersNames = [];
  ToggleHexColor = ToggleHexColor;
  Spacing = Spacing;
  NativeButtonType = NativeButtonType;
  TextInputDirection = TextInputDirection;
  TextInputWidth = TextInputWidth;
  TextInputLabelSize = TextInputLabelSize;
  TextInputVariant = TextInputVariant;
  InputType = InputType;
  InputWidth = InputWidth;
  InputSize = InputSize;
  InputVariant = InputVariant;
  InputFieldSpacing = InputFieldSpacing;
  InputDirection = InputDirection;
  errorMessages = signal({
    invitesPerUser: '',
    codeToJoinMessage: '',
  });
  invitesPerUserInput = signal(
    this.adminTenantService.tenant()?.features?.friendsAndFamily?.config
      ?.invitesPerUser ?? 99
  );

  convertSecondsToMin(value: number): number {
    const minSec = MIN_SESSION_TIMEOUT_SECONDS;
    const maxSec = MAX_SESSION_TIMEOUT_SECONDS;

    if (!value || value < minSec) return minSec / 60;
    if (value > maxSec) return maxSec / 60;

    return Math.round(value / 60);
  }

  convertMinToSeconds(value: number): number {
    const minMin = MIN_SESSION_TIMEOUT_SECONDS / 60;
    const maxMin = MAX_SESSION_TIMEOUT_SECONDS / 60;

    if (!value || value < minMin) {
      return MIN_SESSION_TIMEOUT_SECONDS;
    }
    if (value > maxMin) {
      return MAX_SESSION_TIMEOUT_SECONDS;
    }
    return Math.round(value * 60);
  }

  handleInvitesPerUserChange(value: string) {
    const numValue = Number(value);
    this.invitesPerUserInput.set(numValue);
    this.saveChangesService.setHasChanges(true);
    if (numValue < 1 || numValue > 99) {
      this.errorMessages.update((e) => ({
        ...e,
        invitesPerUser: 'Number of invites must be between 1 and 99.',
      }));
    } else {
      this.errorMessages.update((e) => ({ ...e, invitesPerUser: '' }));
    }
  }

  handleCodeToJoinMessageChange(value: string) {
    this.codeToJoinMessageValue.set(value);
    this.saveChangesService.setHasChanges(true);
    if (value.length > 300) {
      this.errorMessages.update((e) => ({
        ...e,
        codeToJoinMessage: 'Message cannot exceed 300 characters.',
      }));
    } else {
      this.errorMessages.update((e) => ({ ...e, codeToJoinMessage: '' }));
    }
  }

  formatPricesForDropdown(obj: object): object[] {
    const priceArray = [];
    for (const key in obj) {
      priceArray.push({ value: key, label: obj[key] });
    }

    return priceArray;
  }

  ngOnInit() {
    this.saveChangesService.setHasChanges(false);

    const initialFeatures = Object.values(EnterpriseEntityFeatureName).map(
      (key) => ({
        key,
        title: this.translatorService.translate('features.title.' + key),
        value: this.tenantFeatures?.[key]?.value ?? false,
        description: this.translatorService.translate(
          'features.description.' + key
        ),
        config: this.tenantFeatures?.[key]?.config,
      })
    );
    this.featuresConfig.set(initialFeatures);

    this.gympassFeatureConfig.set(
      structuredClone(
        this.adminTenantService.tenant()?.features?.gympass?.config
      )
    );

    if (
      this.adminTenantService.tenant()?.features?.requiresCodeToJoin?.config
        ?.errorMessage
    ) {
      this.codeToJoinMessageValue.set(
        this.adminTenantService.tenant()?.features?.requiresCodeToJoin?.config
          ?.errorMessage
      );
    } else {
      this.codeToJoinMessageValue.set(
        this.translatorService.translate(
          'features.placeholder.requiresCodeToJoinDefault'
        )
      );
    }

    const sessionTimeoutDuration =
      this.adminTenantService.tenant()?.features?.sessionTimeout?.config
        ?.duration;

    if (sessionTimeoutDuration) {
      this.sessionTimeoutValue.set(
        this.convertSecondsToMin(sessionTimeoutDuration)
      );
    }

    if (this.adminTenantService.tenant()?.features?.flex?.config?.flexTile) {
      this.flexTileEnabled = true;
    }

    if (this.adminTenantService.tenant()?.features?.mfa?.config?.pin) {
      this.mfaPinEnabled = true;
    }

    this.loadEmployerProvider();

    if (this.adminTenantService.tenant()?.features?.friendsAndFamily?.value) {
      this.loadFriendsAndFamilyChildTenantConfig();
    }

    this.adminTenantService
      .setTenantPricing()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (error) => {
          this.logger.warn(error);
          const message = this.translatorService.translate(
            'tenantConfiguration.general.getPricingError'
          );
          this.toastService.add(message, ToastType.ERROR);
        },
      });
  }

  loadFriendsAndFamilyChildTenantConfig() {
    this.isLoadingFriendsAndFamilyConfig.set(true);
    this.adminTenantService
      .getFriendsAndFamilyChildTenant()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tenant) => {
          this.friendsAndFamilyTenantGympassConfig.set(
            structuredClone(tenant.features?.gympass?.config)
          );
          this.friendsAndFamilyMonthlyPrice.set(
            tenant.config?.memberPaid?.config?.priceIdMonthly ?? 'None'
          );
          this.friendsAndFamilyTenant.set(tenant);
          this.isLoadingFriendsAndFamilyConfig.set(false);
        },
        error: (error) => {
          this.logger.warn(error);
          this.isLoadingFriendsAndFamilyConfig.set(false);
        },
      });
  }

  loadEmployerProvider() {
    this.isLoadingEmployerProviders.set(true);
    this.adminTenantService
      .getEmployerProvider()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (employerProviders) => {
          this.employerProviders = employerProviders;
          this.employerProvidersNames = employerProviders.map(
            (provider) => provider.name
          );
          const selectedProviderName =
            this.adminTenantService.tenant()?.features?.employerProvider?.config
              ?.name;
          if (
            selectedProviderName &&
            this.employerProvidersNames.includes(selectedProviderName)
          ) {
            this.employerProviderSelected.set(selectedProviderName);
          } else if (this.employerProviders.length > 0) {
            this.employerProviderSelected.set(this.employerProviders[0].name);
          }
          this.isLoadingEmployerProviders.set(false);
        },
        error: (error) => {
          this.logger.warn(error);
          const message = this.translatorService.translate(
            'tenantConfiguration.general.getPricingError'
          );
          this.toastService.add(message, ToastType.ERROR);
          this.isLoadingEmployerProviders.set(false);
        },
      });
  }

  handleCheck(feature: FeatureConfiguration) {
    this.saveChangesService.setHasChanges(true);
    this.featuresConfig.update((features) =>
      features.map((item) => {
        if (item.key === feature.key) {
          return { ...item, value: !item.value };
        }
        return item;
      })
    );
  }

  handleFlexTile() {
    this.flexTileEnabled = !this.flexTileEnabled;
  }

  handleMfa() {
    this.mfaPinEnabled = !this.mfaPinEnabled;
  }

  handleMonthlyPrice(val: string) {
    this.monthlyPrice.set(val);
  }

  handleFriendsAndFamilyMonthlyPrice(val: string) {
    this.friendsAndFamilyMonthlyPrice.set(val);
  }

  handleEmployerProvider(value: string) {
    this.employerProviderSelected.set(value);
  }

  handleGympassTierChange() {
    if (this.gympassFeatureConfig().defaultTier !== this.GympassTier.None) {
      this.monthlyPrice.set(this.GympassTier.None);
    }
  }

  handleFriendsAndFamilyGympassTierChange() {
    if (
      this.friendsAndFamilyTenantGympassConfig().defaultTier !==
      this.GympassTier.None
    ) {
      this.friendsAndFamilyMonthlyPrice.set(this.GympassTier.None);
    }
  }

  handleSave() {
    this.isSaving.set(true);
    this.saveChangesService.setHasChanges(true);
    if (
      this.gympassFeatureConfig()?.defaultTier === this.GympassTier.None &&
      this.monthlyPrice() === this.GympassTier.None
    ) {
      const message = this.translatorService.translate(
        'tenantConfiguration.features.validation.gympassTierNoneMonthlyPriceError'
      );
      this.toastService.add(message, ToastType.ERROR);
      this.isSaving.set(false);
      return;
    }

    const cleanedFeatures = this.featuresConfig().reduce((acc, feature) => {
      acc[feature.key] = {
        value: feature.value,
        config: feature.config,
      };

      if (feature.value) {
        if (feature.key === EnterpriseEntityFeatureName.employerProvider) {
          acc[feature.key].config = this.employerProviders.find(
            (provider) => provider.name === this.employerProviderSelected()
          );
        }

        if (feature.key === EnterpriseEntityFeatureName.flex) {
          acc[feature.key].config = { flexTile: this.flexTileEnabled };
        }

        if (feature.key === EnterpriseEntityFeatureName.requiresCodeToJoin) {
          acc[feature.key].config = {
            errorMessage:
              this.codeToJoinMessageValue() ??
              this.translatorService.translate(
                'features.placeholder.requiresCodeToJoinDefault'
              ),
          };
        }

        if (feature.key === EnterpriseEntityFeatureName.sessionTimeout) {
          acc[feature.key].config = {
            duration: this.convertMinToSeconds(
              Number(this.sessionTimeoutValue())
            ),
          };
        }

        if (
          feature.key === EnterpriseEntityFeatureName.gympass &&
          this.gympassFeatureConfig()
        ) {
          acc[feature.key].config = this.gympassFeatureConfig();
        }

        if (feature.key === EnterpriseEntityFeatureName.mfa) {
          acc[feature.key].config = { pin: this.mfaPinEnabled };
        }

        if (feature.key === EnterpriseEntityFeatureName.friendsAndFamily) {
          const raw = this.invitesPerUserInput();
          const invites = Number.isFinite(+raw) ? Math.trunc(+raw) : 0;
          const childId = this.friendsAndFamilyTenant()?.id;

          acc[feature.key].config = {
            tenantId: childId,
            invitesPerUser: invites,
          };
        }
      }
      return acc;
    }, {});

    const newTenant = {
      ...this.adminTenantService.getTenant(),
      features: {
        ...this.adminTenantService.getTenant().features,
        ...cleanedFeatures,
      },
      config: {
        ...this.adminTenantService.getTenant().config,
        memberPaid: {
          ...(this.adminTenantService.getTenant().config?.memberPaid ?? {}),
          value: this.monthlyPrice() !== this.GympassTier.None,
          config: {
            ...(this.adminTenantService.getTenant().config?.memberPaid
              ?.config ?? {}),
            priceMonthly: this.monthlyPrices()[this.monthlyPrice()],
            priceIdMonthly: this.monthlyPrice(),
          },
        },
      },
    };
    this.adminTenantService
      .putTenant(newTenant)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (newTenant.features.friendsAndFamily?.value) {
            this.saveFriendsAndFamilyTenantSettings()
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: () => {
                  this.showSaveSuccessMessage();
                  this.saveChangesService.setHasChanges(false);
                  this.isSaving.set(false);
                },
                error: (error) => {
                  this.logger.log(error);
                  this.toastService.add(
                    this.translatorService.translate(
                      'tenantConfiguration.features.updateFriendsAndFamilyError'
                    ),
                    ToastType.ERROR
                  );
                  this.saveChangesService.setHasChanges(false);
                  this.isSaving.set(false);
                },
              });
          } else {
            this.showSaveSuccessMessage();
            this.isSaving.set(false);
            this.saveChangesService.setHasChanges(false);
          }
        },
        error: (error) => {
          this.logger.log(error);
          this.toastService.add(
            this.translatorService.translate(
              'tenantConfiguration.features.updateError'
            ),
            ToastType.ERROR
          );
          this.saveChangesService.setHasChanges(false);
          this.isSaving.set(false);
        },
      });
  }

  showSaveSuccessMessage() {
    const message = this.translatorService.translate(
      'tenantConfiguration.features.updateSuccess'
    );
    this.toastService.add(message, ToastType.INFO);
  }

  saveFriendsAndFamilyTenantSettings() {
    return this.adminTenantService.getFriendsAndFamilyChildTenant().pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap((childTenant) => {
        const newGympassConfig = {
          ...this.friendsAndFamilyTenantGympassConfig(),
        };

        const updatedTenant: EnterpriseTenant = {
          ...childTenant,
          features: {
            ...childTenant.features,
            gympass: {
              ...childTenant.features.gympass,
              config: newGympassConfig,
            },
          },
          config: {
            ...childTenant.config,
            memberPaid: {
              ...(childTenant.config?.memberPaid ?? {}),
              value:
                this.friendsAndFamilyMonthlyPrice() !== this.GympassTier.None,
              config: {
                ...(childTenant.config?.memberPaid?.config ?? {}),
                priceMonthly:
                  this.monthlyPrices()[this.friendsAndFamilyMonthlyPrice()],
                priceIdMonthly: this.friendsAndFamilyMonthlyPrice(),
              },
            },
          },
        };
        /*
        If we set the shouldSetTenant param here to true then the child
        tenant will be loaded upon editing friends and family settings
        in the parent tenant which is what we want to avoid, we just want
        to save the child tenant settings.
        */
        return this.adminTenantService.putTenant(updatedTenant, false);
      })
    );
  }

  handleEdit() {
    this.readMode.set(false);
  }

  onSubmit(e: Event) {
    e.preventDefault();
    this.readMode.set(true);
    this.handleSave();
  }
}
